import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import BasicDropdown from 'ember-basic-dropdown/components/basic-dropdown';
import { t } from 'ember-intl';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { eq } from 'ember-truth-helpers';

import Avatar from 'umg-ember-backend/components/ui/avatar';
import greeting from 'umg-ember-backend/helpers/greeting';
import { getBem } from 'umg-ember-backend/utils/get-bem';

import Queue from './queue';
import SelectArtist from './select-artist';
import SelectUser from './select-user';
import styles from './styles.module.css';

const bem = getBem(styles);

export default class ApplicationHeader extends Component {
  // Services
  @service permission;
  @service router;
  @service locale;

  // Defaults
  @tracked isLanguageSwitchOpen = false;

  // Functions
  signOut = () => {
    this.router.transitionTo('admin.sign-out');
  };

  toggleLanguageSwitch = () => {
    this.isLanguageSwitchOpen = !this.isLanguageSwitchOpen;
  };

  setupLocale = (code) => {
    this.locale.setup(code);
    this.router.refresh();
  };

  // Template
  <template>
    <header class={{(bem)}} ...attributes>
      <div
        class={{bem 'toggle'}}
        role='button'
        {{on 'click' @toggleNavigation}}
      >
        <svg class={{bem 'toggle-icon'}} viewBox='0 0 24 24' aria-hidden='true'>
          <rect x='3' y='4' width='18' height='16' rx='2' />
          <path
            d={{if
              @isNavigationOpen
              'M9 4v16M16 9l-3 3 3 3'
              'M9 4v16M13 9l3 3-3 3'
            }}
          />
        </svg>
      </div>

      <div class={{bem 'global-filter'}}>
        <SelectArtist />
        {{! <SelectChannel /> }}
        <SelectUser />
      </div>

      <div class={{bem 'spacer'}}></div>

      <BasicDropdown @horizontalPosition='right' as |dropdown|>
        <dropdown.Trigger class={{bem 'profile'}}>
          <div class={{bem 'greet'}}>
            {{greeting this.permission.user.person}}
          </div>
          <Avatar
            @user={{this.permission.user}}
            @tooltip={{false}}
            class={{bem 'avatar'}}
          />
        </dropdown.Trigger>

        <dropdown.Content class={{bem 'navigation'}}>
          <LinkTo @route='admin.profile' class={{bem 'item'}}>
            {{svgJar
              'profile'
              class=(bem 'icon')
              aria-hidden='true'
              focusable='false'
              data-test-application-header-icon='profile'
            }}
            <div class={{bem 'label'}}>
              {{t 'application.header.profil'}}
            </div>
          </LinkTo>
          <hr class={{bem 'hr'}} />

          <button
            class={{bem 'item'}}
            type='button'
            {{on 'click' this.toggleLanguageSwitch}}
          >
            {{svgJar
              'language'
              class=(bem 'icon')
              aria-hidden='true'
              focusable='false'
              data-test-application-header-icon='language'
            }}
            <div class={{bem 'label'}}>
              {{t 'application.header.select_language'}}
            </div>
            <div class={{bem 'status'}}>
              {{this.locale.currentLocale.name}}
            </div>
          </button>
          <div
            class={{bem
              'language-switch'
              (hash is-open=this.isLanguageSwitchOpen)
            }}
          >
            {{#each this.locale.availableLocales as |locale|}}
              <button
                class={{bem 'item'}}
                type='button'
                {{on 'click' (fn this.setupLocale locale.code)}}
                {{on 'click' this.toggleLanguageSwitch}}
              >
                <div class={{bem 'icon'}}>
                  {{locale.flag}}
                </div>
                <div class={{bem 'label'}}>
                  {{locale.name}}
                </div>
                {{#if (eq locale.code this.locale.currentLocale.code)}}
                  <div class={{bem 'status'}}>
                    {{svgJar
                      'selected'
                      class=(bem 'check')
                      aria-hidden='true'
                      focusable='false'
                    }}
                  </div>
                {{/if}}
              </button>
            {{/each}}
          </div>
          <hr class={{bem 'hr'}} />

          <button
            class={{bem 'item' (hash color='danger')}}
            type='button'
            {{on 'click' this.signOut}}
          >
            {{svgJar
              'sign-out'
              class=(bem 'icon')
              aria-hidden='true'
              focusable='false'
              data-test-application-header-icon='sign-out'
            }}
            <div class={{bem 'label'}}>
              {{t 'application.header.logout'}}
            </div>
          </button>
        </dropdown.Content>
      </BasicDropdown>

      <Queue />
    </header>
  </template>
}
